import { createContext, useContext, type ComponentProps } from "react";
import { Streamdown, type Components, type ExtraProps } from "streamdown";
import { createMathPlugin } from "@streamdown/math";
import "streamdown/styles.css";
import "katex/dist/katex.min.css";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IconLoader2, IconNews } from "@tabler/icons-react";
import { FigureCard } from "./ui/FigureCard";
import { CitationChip } from "./ui/CitationChip";
import { HoverCard } from "./ui/HoverCard";
import { preprocessScientificNotation } from "./ScientificText";
import { cn } from "../lib/utils";

// Whether the answer is still streaming, read by the citation-chip renderer.
// A Context (not a `components`-prop swap) because Streamdown memoizes already
// rendered blocks in streaming mode and does NOT re-render them when the
// `components` object identity changes — so a chip rendered while streaming
// would stay frozen in its loading state after the stream ends. Context
// propagation bypasses `React.memo` block boundaries, so each chip flips to its
// clickable state the moment streaming finishes.
const StreamingContext = createContext(false);

/**
 * What a citation link resolves to. Declared here rather than imported from the
 * chat domain — this component only needs to know how a citation is chipped and
 * what tapping it opens, not what a source is.
 *
 * `listed` — the turn's source list holds it, so the chip is the NUMBER of its
 * row. `unlisted` — it does not (the model cited a URL retrieval never
 * returned), so the chip keeps the label the answer wrote. Both open the source
 * sheet: a citation explains itself in the sheet however little of it resolved,
 * and never navigates the reader out of the conversation on its own.
 */
type ResolvedCitation =
  | {
      kind: "listed";
      number: number;
      onOpen: () => void;
      // What the citation's hover card shows (desktop), or null for none.
      preview: React.JSX.Element | null;
    }
  | { kind: "unlisted"; onOpen: () => void };

/**
 * What a "tag" call site hands down to resolve the citations in its markdown.
 * The label rides along with the href because it is all an unlisted citation
 * has to state itself with. Null resolves nothing at all — the surface numbers
 * no sources (see `noCitations`), so its citations stay plain links.
 */
export type CitationResolver = (citation: {
  href: string;
  label: string;
}) => ResolvedCitation | null;

// Carried by Context for the same reason `StreamingContext` is: Streamdown
// memoizes rendered blocks, so a resolver carried on the `components` object
// would freeze into the blocks written before the turn's sources were known.
// The default resolves nothing — every "tag" call site passes its own through
// the provider below.
const CitationContext = createContext<CitationResolver>(() => null);

// LLMs emit math with LaTeX delimiters `\(…\)` (inline) and `\[…\]` (display),
// but remark-math (which the Streamdown math plugin builds on) only recognises
// `$…$` / `$$…$$` — and worse, markdown eats the backslash of `\(`/`\[` (they
// are valid escapes), leaving bare `(`/`[`. Run this on the RAW string
// (backslashes still present) to convert to the dollar delimiters KaTeX
// expects. Replacement functions return literal `$`/`$$` (a plain string
// replacement would treat `$$` as a special pattern).
function normalizeMathDelimiters(text: string): string {
  return text
    .replace(/\\\[/g, () => "$$")
    .replace(/\\\]/g, () => "$$")
    .replace(/\\\(/g, () => "$")
    .replace(/\\\)/g, () => "$");
}

// Inline single-`$` math is off by default in the plugin; enable it because we
// convert `\(…\)` → `$…$` above. Built once at module scope (stateless).
const mathPlugin = createMathPlugin({ singleDollarTextMath: true });

// One markdown link. The destination allows one level of BALANCED parentheses
// because every Elsevier/Lancet DOI carries a pair
// (`10.1016/S2352-3018(26)00059-7`), and `[^)]+` would end the link at the first
// one — leaving the group unrecognised and its tail in the prose. The two
// alternatives are disjoint on their first character, so the group cannot
// backtrack.
const CITATION_LINK = String.raw`\[[^\]]+\]\((?:[^\s()]|\([^\s()]*\))*\)`;
// Citations stream as a parenthesized, `;`-separated list of markdown links,
// e.g. `…life ([A](u); [B](u)). It is…`. They render as standalone chips
// (linkVariant "tag"), so reshape the group: drop the wrapping parentheses and
// the `;` separators, move the trailing sentence period to BEFORE the chips,
// and hard-break the line after the last chip so the following prose starts on
// its own line. The inner content must be entirely links, so prose parens
// ("(SFE)", "(LINC 3)") are untouched; requiring the closing `)` keeps
// half-streamed text safe.
const CITATION_GROUP = new RegExp(
  String.raw`\s*\(\s*((?:${CITATION_LINK}\s*;\s*)*${CITATION_LINK})\s*\)(\.?)`,
  "g"
);

function formatCitations(text: string): string {
  return text.replace(
    CITATION_GROUP,
    (_match: string, links: string, period: string) => {
      const joined = links.replace(/\s*;\s*/g, " ");
      const lead = period === "." ? ". " : " ";
      return `${lead}${joined}  \n`;
    }
  );
}

type LinkVariant = "anchor" | "tag";

/**
 * A citation rendered as the DS "Tag" (Figma node 6079:2013): a mint chip led
 * by the source journal's logo tile. Until the transcript carries
 * per-source logos, the tile holds the Tabler journal glyph.
 *
 * `loading` — the answer is still streaming, so the href may be half-emitted:
 * the chip shows a spinner and swallows clicks (`pointer-events-none`) so the
 * wrapping link cannot navigate to an unfinished source.
 */
function CitationLabelChip({
  children,
  state,
}: {
  children: React.ReactNode;
  state: "idle" | "loading";
}): React.JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-lg bg-ds-mint-50 px-2 py-1 align-middle font-dm-sans text-footnote font-medium whitespace-nowrap text-ds-mint-800",
        state === "loading" && "pointer-events-none opacity-60"
      )}
    >
      <span className="flex size-4 shrink-0 items-center justify-center rounded-sm bg-ds-mint-100">
        {state === "loading" ? (
          <IconLoader2 stroke={1.5} className="size-3 animate-spin" />
        ) : (
          <IconNews stroke={1.5} className="size-3" />
        )}
      </span>
      {children}
    </span>
  );
}

// Figures an answer embeds as markdown images. Rendered as the DS Figure Card
// rather than a bare `<img>`; the alt text is the caption.
function AnswerFigure(props: ComponentProps<"img"> & ExtraProps) {
  const { t } = useTranslation();
  const { src, alt } = props;
  if (src === undefined || src === "") return null;
  return (
    <FigureCard
      src={src}
      caption={alt === undefined || alt === "" ? null : alt}
      label={t("Figure")}
    />
  );
}

// Internal juisci.com links route through React Router; external links open in
// a new tab. Per `linkVariant`: plain anchors/links in prose ("anchor"), or — in
// chat answers where links are citations — a `CitationLabelChip` ("tag"), identical
// for both so a citation reads the same wherever the source lives; only the
// wrapper differs. (`node` from Streamdown's ExtraProps rides along in the rest
// spread, mirroring the previous react-markdown handler.)
/**
 * The in-app route a markdown link points at, or null when it leaves the app.
 *
 * TWO shapes count as internal. An absolute `juisci.com` URL, which is what an
 * LLM answer and a stored citation carry. And a ROOT-RELATIVE path, which is
 * what a human authoring markdown writes for an in-app destination
 * (`[the method](/gpt)`) — without that arm it fell through to a plain anchor,
 * and Streamdown's link hardening then stamped `target="_blank"` on it, so an
 * in-app link opened a new tab and, inside the iOS and Android webviews, could
 * leave the app altogether.
 *
 * `//evil.com` is protocol-relative, not root-relative: it reads as a path and
 * is a different origin, so it is excluded.
 */
function toInternalPath(href: string | undefined): string | null {
  if (href === undefined) return null;
  if (href.startsWith("//")) return null;
  if (href.startsWith("/")) return href;
  if (href.includes("juisci.com")) return href.split("juisci.com").at(1) ?? "";
  return null;
}

function makeComponents(linkVariant: LinkVariant): Components {
  function Anchor(props: ComponentProps<"a"> & ExtraProps) {
    // Read unconditionally (rules-of-hooks); only the "tag" branch uses them.
    const streaming = useContext(StreamingContext);
    const resolveCitation = useContext(CitationContext);
    const { t } = useTranslation();
    const { children, href, ...otherProps } = props;
    const internalPath = toInternalPath(href);

    if (linkVariant === "tag") {
      // While the answer is still streaming, a citation's href may be
      // half-emitted and the source not final — render a non-interactive
      // loading chip (no Link/anchor wrapper → no navigation) and swap the
      // journal glyph for a spinner until the stream completes.
      if (streaming) {
        return (
          <CitationLabelChip state="loading">{children}</CitationLabelChip>
        );
      }
      // Every citation of a surface that resolves them opens the source SHEET,
      // and none of them is a link: the sheet is what carries a source's
      // identity and its way out, so a citation can never both jump away and
      // explain itself. A listed source renders as its NUMBER, the same chip the
      // Sources tab draws beside its row; one the list does not hold keeps the
      // label the answer wrote, and its sheet states what the link alone says.
      // The label is the link's own text — markdown writes a citation label as
      // plain text, and a non-text label leaves the sheet to fall back on the
      // URL.
      const citation =
        href === undefined
          ? null
          : resolveCitation({
              href,
              label: typeof children === "string" ? children : "",
            });
      if (citation !== null) {
        const chipClass =
          "inline-flex cursor-pointer border-none bg-transparent p-0 align-middle";
        switch (citation.kind) {
          case "listed": {
            const chip = (
              <button
                type="button"
                onClick={citation.onOpen}
                className={chipClass}
              >
                <CitationChip number={citation.number} size="sm" />
              </button>
            );
            return citation.preview === null ? (
              chip
            ) : (
              <HoverCard
                label={t("Source {{number}}", { number: citation.number })}
                trigger={chip}
                className="w-80"
              >
                {citation.preview}
              </HoverCard>
            );
          }
          case "unlisted":
            return (
              <button
                type="button"
                onClick={citation.onOpen}
                className={chipClass}
              >
                <CitationLabelChip state="idle">{children}</CitationLabelChip>
              </button>
            );
          default:
            throw new Error(citation satisfies never);
        }
      }

      // A surface that numbers no sources at all (`noCitations`): there is no
      // sheet behind any of its citations, so the labelled chip keeps its link.
      const chip = (
        <CitationLabelChip state="idle">{children}</CitationLabelChip>
      );
      // Same chip for both — only the wrapper differs (soft route vs new tab).
      if (internalPath !== null) {
        // Internal sources route IN-APP via React Router — NEVER spread
        // `otherProps` here: Streamdown's link hardening injects
        // `target="_blank"` / `rel` on every link, and a `<Link target="_blank">`
        // makes the browser open a new tab (full reload), which both leaves the
        // conversation and breaks the article Back button (it falls back to
        // Discovery because the new tab has no client history to go back to).
        return (
          <Link
            to={internalPath}
            className="inline-flex no-underline align-middle pb-0.5!"
          >
            {chip}
          </Link>
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex no-underline align-middle pb-0.5!"
          {...otherProps}
        >
          {chip}
        </a>
      );
    }
    if (internalPath !== null) {
      // In-app navigation — drop `otherProps` so Streamdown's injected
      // `target="_blank"` can't turn this soft route into a new-tab reload.
      return <Link to={internalPath}>{children}</Link>;
    }
    return (
      <a href={href} {...otherProps}>
        {children}
      </a>
    );
  }
  // Figures are a chat-answer concern: prose call sites (legal text, article
  // bodies) keep the plain `<img>` Streamdown renders.
  return linkVariant === "tag"
    ? { a: Anchor, img: AnswerFigure }
    : { a: Anchor };
}

const anchorComponents = makeComponents("anchor");
const tagComponents = makeComponents("tag");

// `streaming` is only meaningful when links render as citation chips, so it is
// part of the "tag" arm only — an "anchor" call site (prose, legal text) cannot
// pass it, making the impossible "streaming prose" state unrepresentable.
type ContentMarkdownProps = {
  className?: string;
  // Render LaTeX math (`$…$` / `$$…$$`) via KaTeX. Required (not defaulted) so
  // every call site declares intent — math is opt-in because most content
  // (legal text, etc.) has no math and a literal `$` must not become math.
  math: boolean;
  children: string;
} & (
  | { linkVariant: "anchor" }
  // "tag": chat answers, citations as Tag chips. `streaming` gates the chips
  // into a non-interactive loading state until the answer finishes streaming,
  // and `citation` turns a cited link into the numbered chip its row carries in
  // the Sources tab. Required, not defaulted: a surface with no numbered list
  // behind it (the onboarding opener) has to say so with `noCitations`.
  | {
      linkVariant: "tag";
      streaming: boolean;
      citation: CitationResolver;
    }
);

/**
 * The resolver for an answer that has no numbered source list behind it. Named
 * (not an inline `() => null`) so a call site declares the absence rather than
 * quietly passing a stub, and so the identity is stable across renders.
 */
export function noCitations(): null {
  return null;
}

export default function ContentMarkdown(props: ContentMarkdownProps) {
  const { className = "", math, children } = props;
  // Citation reshaping is coupled to the chip rendering — both gated on "tag".
  const source =
    props.linkVariant === "tag" ? formatCitations(children) : children;
  return (
    <StreamingContext.Provider
      value={props.linkVariant === "tag" ? props.streaming : false}
    >
      <CitationContext.Provider
        value={props.linkVariant === "tag" ? props.citation : noCitations}
      >
        <Streamdown
          className={"markdown-content " + className}
          components={
            props.linkVariant === "tag" ? tagComponents : anchorComponents
          }
          plugins={math ? { math: mathPlugin } : {}}
          // `preprocessScientificNotation` injects raw `<sup>`/`<sub>`, which
          // Streamdown's HTML sanitization strips unless whitelisted. With math on
          // KaTeX handles sub/superscripts inside `$…$`, so we skip the preprocess
          // and need no extra tags.
          allowedTags={math ? {} : { sup: [], sub: [] }}
        >
          {math
            ? normalizeMathDelimiters(source)
            : preprocessScientificNotation(source)}
        </Streamdown>
      </CitationContext.Provider>
    </StreamingContext.Provider>
  );
}
